import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, onSnapshot, query, doc, updateDoc, 
  setDoc, Timestamp, where, orderBy, deleteDoc
} from 'firebase/firestore';
import Swal from 'sweetalert2';
import moment from 'moment';

function Nozzlereading() {
  const [fuelType, setFuelType] = useState("MS"); // Toggle state: MS or HSD
  const [orderdate, setOrderdate] = useState(moment().format("YYYY-MM-DD"));
  const [nozzles, setNozzles] = useState([]);
  const [dailyData, setDailyData] = useState({});
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const userId = auth.currentUser?.uid;

  const Toast = Swal.mixin({
    toast: true, position: 'top-end', showConfirmButton: false, timer: 3000,
  });

  // 1. Fetch Nozzles filtered by current fuelType (MS or HSD)
  useEffect(() => {
    if (!userId) return;
    const q = query(
      collection(db, "nozzles"), 
      where("userId", "==", userId), 
      where('fuelType', "==", fuelType), 
      orderBy('nozzleName', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      const nozzleList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setNozzles(nozzleList);
      
      const initialForm = {};
      nozzleList.forEach(n => {
        initialForm[`${n.id}_opening`] = n.lastReading || 0;
        initialForm[`${n.id}_rate`] = n.currentRate || 0;
        initialForm[`${n.id}_closing`] = "";
        initialForm[`${n.id}_testing`] = 0;
      });
      setDailyData(initialForm);
    });
    return () => unsubscribe();
  }, [userId, fuelType]);

  // 2. Fetch History filtered by fuelType
  useEffect(() => {
    if (!userId) return;
    const q = query(
      collection(db, "daily_ledger"), 
      where("userId", "==", userId),
      where("fuelType", "==", fuelType)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setHistory(data.sort((a, b) => b.date.localeCompare(a.date)));
    });
    return () => unsubscribe();
  }, [userId, fuelType]);

  const handleInputChange = (field, value) => {
    setDailyData(prev => ({ ...prev, [field]: value }));
  };

  const deleteEntry = async (id) => {
    const result = await Swal.fire({
      title: 'Delete this record?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes'
    });
    if (result.isConfirmed) {
      await deleteDoc(doc(db, "daily_ledger", id));
      Toast.fire({ icon: 'success', title: 'Deleted' });
    }
  };

  const saveDailyReading = async (e) => {
    e.preventDefault();
    if (!userId) return;
    setLoading(true);
    try {
      let totalLiters = 0;
      let totalAmount = 0;
      
      const nozzleSales = nozzles.map(n => {
        const op = parseFloat(dailyData[`${n.id}_opening`] || 0);
        const cl = parseFloat(dailyData[`${n.id}_closing`] || 0);
        const ts = parseFloat(dailyData[`${n.id}_testing`] || 0);
        const rt = parseFloat(dailyData[`${n.id}_rate`] || 0);
        
        // Exact raw numeric sale calculation
        const sale = cl > 0 ? (cl - op - ts) : 0;
        const amount = sale * rt;
        
        if (cl > 0) { 
          totalLiters += sale; 
          totalAmount += amount; 
        }
        
        return { nozzleId: n.id, name: n.nozzleName, op, cl, ts, sale, rate: rt, amount };
      }).filter(s => s.cl > 0);

      if (nozzleSales.length === 0) throw new Error("Enter closing values.");

      const ledgerId = `${userId}_${orderdate}_${fuelType}`;
      await setDoc(doc(db, "daily_ledger", ledgerId), {
        userId, 
        date: orderdate, 
        fuelType, 
        nozzleSales, 
        totalSales: totalLiters, 
        totalRevenue: totalAmount, 
        updatedAt: Timestamp.now()
      }, { merge: true });

      for (const sale of nozzleSales) {
        await updateDoc(doc(db, "nozzles", sale.nozzleId), { lastReading: sale.cl });
      }
      Toast.fire({ icon: 'success', title: `${fuelType} Saved!` });
    } catch (err) {
      Toast.fire({ icon: 'error', title: err.message });
    } finally { setLoading(false); }
  };

  const filteredHistory = history.filter(h => h.date.includes(searchTerm));
  const currentItems = filteredHistory.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

  return (
    <div className="content-wrapper">
      <section className="content pt-2">
        <div className="container-fluid">
          
          {/* FUEL SELECTION TABS */}
          <div className="btn-group btn-group-toggle w-100 mb-2 shadow-sm">
            <label className={`btn btn-sm ${fuelType === 'MS' ? 'btn-success' : 'btn-light'}`}>
              <input type="radio" onClick={() => {setFuelType('MS'); setCurrentPage(1);}} /> MS (Petrol)
            </label>
            <label className={`btn btn-sm ${fuelType === 'HSD' ? 'btn-primary' : 'btn-light'}`}>
              <input type="radio" onClick={() => {setFuelType('HSD'); setCurrentPage(1);}} /> HSD (Diesel)
            </label>
          </div>

          {/* COMPACT ENTRY FORM */}
          <div className={`card card-outline shadow-sm mb-2 ${fuelType === 'MS' ? 'card-success' : 'card-primary'}`}>
            <div className="card-header py-1">
              <h3 className="card-title text-xs text-bold">{fuelType} Register - {moment(orderdate).format('DD/MM')}</h3>
              <input type="date" className="form-control form-control-sm float-right w-25" 
                value={orderdate} onChange={e => setOrderdate(e.target.value)} />
            </div>
            <div className="card-body p-0">
              <table className="table table-sm table-bordered m-0 text-center text-xs">
                <thead className="bg-light">
                  <tr>
                    <th>Nozzle</th>
                    <th>Rate</th>
                    <th>Opening Reading</th>
                    <th>Closing Reading</th>
                    <th>TESTING</th>
                    <th className="bg-gray-light">Total Liters</th>
                    <th className="bg-dark text-white">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {nozzles.map(n => {
                    const op = parseFloat(dailyData[`${n.id}_opening`] || 0);
                    const cl = parseFloat(dailyData[`${n.id}_closing`] || 0);
                    const ts = parseFloat(dailyData[`${n.id}_testing`] || 0);
                    const rt = parseFloat(dailyData[`${n.id}_rate`] || 0);
                    
                    const saleNumeric = cl > 0 ? (cl - op - ts) : 0;
                    const amountNumeric = saleNumeric * rt;
                    
                    return (
                      <tr key={n.id}>
                        <td className="align-middle"><b>{n.nozzleName}</b></td>
                        <td className="p-0">
                          <input type="number" className="form-control form-control-xs text-center border-0" value={rt} onChange={(e) => handleInputChange(`${n.id}_rate`, e.target.value)} />
                        </td>
                        <td className="align-middle">{op}</td>
                        <td className="p-0">
                          <input type="number" className="form-control form-control-xs text-center" value={dailyData[`${n.id}_closing`]} onChange={(e) => handleInputChange(`${n.id}_closing`, e.target.value)} />
                        </td>
                        <td className="p-0">
                          <input type="number" className="form-control form-control-xs text-center" value={dailyData[`${n.id}_testing`]} onChange={(e) => handleInputChange(`${n.id}_testing`, e.target.value)} />
                        </td>
                        <td className="align-middle bg-light"><b>{saleNumeric.toFixed(2)}</b></td>
                        <td className="align-middle bg-dark text-white">{amountNumeric.toFixed(0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <button onClick={saveDailyReading} disabled={loading} className={`btn btn-xs btn-block py-2 ${fuelType === 'MS' ? 'btn-success' : 'btn-primary text-bold'}`}>
                {loading ? '...' : `SAVE ${fuelType} SALES`}
              </button>
            </div>
          </div>

          {/* HISTORY TABLE */}
          <div className="card shadow-sm">
            <div className="card-header py-1 bg-navy">
              <h3 className="card-title text-xs mt-1">{fuelType} Log</h3>
              <div className="card-tools">
                <input type="text" className="form-control form-control-sm h-75" placeholder="Search Date..." 
                  onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
            </div>
            <div className="card-body p-0">
              <table className="table table-xs table-bordered text-center m-0">
                <thead className="bg-secondary text-xs">
                  <tr>
                    <th>Date</th>
                    {nozzles.map(n => <th key={n.id}>{n.nozzleName}</th>)}
                    <th className="bg-success">Total L</th>
                    <th className="bg-success text-white">Amt</th>
                    <th><i className="fas fa-cog"></i></th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {currentItems.map(row => (
                    <tr key={row.id}>
                      <td className="align-middle"><b>{moment(row.date).format('DD-MMM')}</b></td>
                      {nozzles.map(n => <td key={n.id} className="align-middle">{row.nozzleSales?.find(x => x.nozzleId === n.id)?.sale?.toFixed(2) || '-'}</td>)}
                      <td className="align-middle">{row.totalSales?.toFixed(2)}</td>
                      <td className="align-middle"><b>₹{row.totalRevenue?.toFixed(0)}</b></td>
                      <td className="p-0 align-middle">
                        <button onClick={() => deleteEntry(row.id)} className="btn btn-link text-danger btn-xs"><i className="fas fa-trash"></i></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Nozzlereading;